import { Flag, Info, Pencil, Trash2 } from "lucide-react";
import { JobTypeChip } from "@/components/ui/JobTypeChip";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { TypeChip } from "@/components/ui/TypeChip";
import type { TimeEntry } from "@/hooks/use-time-entries";
import { OVERHEAD } from "@/lib/usg-constants";

interface HistoryEntryRowProps {
  entry: TimeEntry;
  onEdit: (entry: TimeEntry) => void;
  onDelete: (entry: TimeEntry) => void;
  onResolveFlag: (entry: TimeEntry) => void;
}

function formatHours(n: number) {
  return (Math.round(n * 10) / 10).toFixed(1);
}

export function HistoryEntryRow({ entry, onEdit, onDelete, onResolveFlag }: HistoryEntryRowProps) {
  const isOverhead = entry.type === "overhead";
  const oh = isOverhead ? OVERHEAD.find((o) => o.code === entry.overheadCode) : null;

  const primary = isOverhead
    ? `${oh?.code ?? entry.overheadCode ?? ""} · ${oh?.name ?? ""}`
    : (entry.woNumber ?? "—");

  const secondary = isOverhead ? (entry.overheadSub ?? "—") : "";
  const categoryText = isOverhead
    ? entry.overheadSub
      ? `Overhead › ${entry.overheadSub}`
      : "Overhead"
    : `${entry.category ?? ""} › ${entry.subtask ?? ""}`;

  const isDraft = entry.status === "draft";
  const isFlagged = entry.status === "flagged";

  return (
    <div className={`entry-row${isFlagged ? " is-flagged" : ""}`}>
      <div className="entry-row__wo">
        <span className={`entry-row__wo-primary${isOverhead ? " is-overhead" : ""}`}>
          {primary}
        </span>
        {secondary && <span className="entry-row__wo-secondary">{secondary}</span>}
      </div>
      <div className="entry-row__category" title={categoryText}>
        {categoryText}
      </div>
      <div className="entry-row__hours">{formatHours(entry.hours)} hrs</div>
      <div>
        {isOverhead ? <TypeChip type="overhead" /> : <JobTypeChip type={entry.category ?? ""} />}
      </div>
      <div className="entry-row__status-cell">
        <StatusBadge status={entry.status as "draft" | "submitted" | "approved" | "flagged"} />
        {isFlagged && entry.flagManager && (
          <span className="flag-info">
            <Info size={14} />
            <span className="flag-info__tip">
              <strong>Flagged by {entry.flagManager}</strong>
              {entry.flagNote}
            </span>
          </span>
        )}
      </div>
      <div className="entry-row__actions">
        {isFlagged ? (
          <button
            type="button"
            className="icon-btn"
            title="Resolve flagged entry"
            onClick={() => onResolveFlag(entry)}
          >
            <Flag size={15} />
          </button>
        ) : (
          <button
            type="button"
            className="icon-btn"
            title={isDraft ? "Edit entry" : "Locked — only drafts are editable"}
            disabled={!isDraft}
            onClick={() => onEdit(entry)}
          >
            <Pencil size={15} />
          </button>
        )}
        <button
          type="button"
          className="icon-btn icon-btn--danger"
          title={isDraft ? "Delete entry" : "Only drafts can be deleted"}
          disabled={!isDraft}
          onClick={() => onDelete(entry)}
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}
